// ============================================================================
// YardFilter.jsx — pick any number of yards, not one.
//
// The filter was a single <select>: one yard or all of them. Reviewers compare
// a handful against each other far more often than either, and that took three
// page loads. Sixteen-odd yards carry shifts, which is too many for a row of
// checkboxes and too many for a native multi-select nobody knows to
// ctrl-click, so it is a panel.
//
// Selection is a LIST, and empty means all — the same thing omitting the
// parameter means on the server. There is no separate "all" flag to disagree
// with the list.
//
// Hover and focus need real CSS, which an inline style object cannot express
// (it is why the mobile nav silently never worked), so this file carries a
// <style> block like Login.jsx and AppShell.jsx do.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { c, t, sp, r, card } from '../../theme';

const css = `
.twa-yard-panel { max-height: 280px; overflow-y: auto; }
.twa-yard-opt { display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 6px 10px; border: 0; background: none; text-align: left;
  font: inherit; color: inherit; cursor: pointer; }
.twa-yard-opt:hover { background: ${c.surfaceSunk}; }
.twa-yard-opt:focus-visible { outline: 2px solid ${c.info}; outline-offset: -2px; }
.twa-yard-btn:hover { border-color: ${c.textFaint}; }
`;

export default function YardFilter({ options = [], selected = [], onChange }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  // Close on an outside click or Escape. Without this the panel sits over the
  // list and swallows the next thing the reader tries to click, which reads as
  // the page having frozen.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const key  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [open]);

  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };

  const label = selected.length === 0
    ? 'All yards'
    : selected.length === 1
      ? (options.find((o) => o.id === selected[0])?.name || '1 yard')
      : `${selected.length} yards`;

  return (
    <div style={s.wrap} ref={box}>
      <style>{css}</style>
      <button type="button" className="twa-yard-btn" style={s.button}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open} aria-haspopup="listbox">
        <span style={s.buttonLabel}>{label}</span>
        <span aria-hidden="true" style={s.caret}>▾</span>
      </button>

      {open && (
        <div style={s.panel} role="listbox" aria-multiselectable="true">
          <button type="button" className="twa-yard-opt" onClick={() => onChange([])}
                  role="option" aria-selected={selected.length === 0}>
            <input type="checkbox" readOnly checked={selected.length === 0} tabIndex={-1} />
            <span style={s.allLabel}>All yards</span>
          </button>
          <div style={s.rule} />
          <div className="twa-yard-panel">
            {options.map((o) => (
              <button key={o.id} type="button" className="twa-yard-opt"
                      onClick={() => toggle(o.id)}
                      role="option" aria-selected={selected.includes(o.id)}>
                <input type="checkbox" readOnly checked={selected.includes(o.id)} tabIndex={-1} />
                <span style={s.optName}>{o.name}</span>
                <span style={s.optCode}>{o.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:   { position: 'relative' },
  button: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: sp[2],
            padding: '7px 10px', border: `1px solid ${c.border}`, borderRadius: r.sm,
            background: c.surface, fontSize: '13px', color: c.text, fontFamily: 'inherit',
            minWidth: '200px', cursor: 'pointer' },
  buttonLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  caret:  { ...t.micro, color: c.textFaint, flexShrink: 0 },
  panel:  { ...card.base, position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
            background: c.surface, border: `1px solid ${c.border}`, minWidth: '260px',
            boxShadow: '0 6px 16px rgba(17,24,39,0.12)', padding: `${sp[1]} 0` },
  rule:   { height: 1, background: c.border, margin: `${sp[1]} 0` },
  allLabel: { ...t.bodySm, fontWeight: 600 },
  optName:  { ...t.bodySm, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  optCode:  { ...t.micro, ...t.tabular, color: c.textFaint, flexShrink: 0 },
};
